import {useState} from 'react';
import {I18n} from 'aws-amplify/utils';
import {
    ActionIcon,
    Alert,
    Button,
    Group,
    Select,
    Stack,
    Text,
    TextInput,
} from '@mantine/core';

import {CertificateConnection} from '../services/CertificatesService';
import {
    CertificateFormValues,
    CLOUDFRONT_REGION,
    COMMON_REGIONS,
    toRequestInput,
    validateCertificateForm,
} from './certificateFormValues';
import {
    CertificateTargetType,
    RequestCertificateInput,
    wildcardCoverageNote,
} from '../types';

// FNP-1193 — THE REQUEST FORM.
//
// ⚠️ THIS FORM CARRIES THE WILDCARD EXPLANATION, and where it appears is the
// point of the criterion.
//
// The ticket is explicit: a wildcard matches exactly one label, and rather than
// treating that as a reason to talk a user out of a deeper hierarchy, let them
// request a certificate for the deeper name directly and explain the constraint
// at the point in the interface where it applies.
//
// So the note appears UNDER THE HOSTNAME FIELD, as the operator types a name
// the rule bites on, and it is worded as an explanation with a next step —
// never as a warning, never as a block, and never as advice to flatten their
// naming. See `wildcardCoverageNote`.
export const CertificateRequestForm = (
    {
        initialValues,
        connections,
        isSubmitting,
        onSubmit,
        onCancel,
    }: {
        initialValues: CertificateFormValues,
        // Already filtered to the connections that pass the backend's own gate.
        connections: CertificateConnection[],
        isSubmitting: boolean,
        onSubmit: (input: RequestCertificateInput) => Promise<void>,
        onCancel: () => void,
    },
) => {

    const [values, setValues] = useState<CertificateFormValues>(initialValues);
    // Errors appear only after a submit attempt. Marking a half-typed hostname
    // invalid on the first keystroke is noise, not help.
    const [errors, setErrors] = useState<Record<string, string>>({});

    const handleChange = (next: Partial<CertificateFormValues>) => {
        setValues(current => ({
            ...current,
            ...next,
        }));
    };

    const handleSubmit = async () => {

        const found = validateCertificateForm(values);
        setErrors(found);

        if (Object.keys(found).length > 0) {
            return;
        }

        // ⚠️ AWAITED, AND THE REJECTION IS NOT CAUGHT. The mutation's own
        // onError has already classified and notified it; catching it again
        // would swallow the signal that keeps the drawer open.
        await onSubmit(toRequestInput(values));
    };

    // ⚠️ THE CONSTRAINT EXPLANATION, RECOMPUTED AS THEY TYPE. Undefined when
    // there is nothing worth saying, so this is not a permanent lecture.
    const coverageNote = wildcardCoverageNote(values.domainName, I18n.get);

    const isCloudFront = values.targetType === 'CLOUDFRONT';

    const targetOptions: { value: CertificateTargetType, label: string }[] = [
        {value: 'UNSPECIFIED', label: I18n.get('Not specified — just managing the certificate')},
        {value: 'CLOUDFRONT', label: I18n.get('CloudFront distribution')},
        {value: 'LOAD_BALANCER', label: I18n.get('Load balancer')},
        {value: 'API_GATEWAY', label: I18n.get('API Gateway')},
    ];

    return (
        <Stack gap={'md'}>

            <Select
                required={true}
                label={I18n.get('Connection')}
                description={I18n.get('The certificate is issued in your own account, under this connection\'s credential. Its zone also carries the validation record.')}
                placeholder={I18n.get('Choose a connection')}
                data={connections.map(connection => ({
                    value: connection.id,
                    label: `${connection.displayName} (${connection.providerId})`,
                }))}
                value={values.connectionId || null}
                onChange={value => handleChange({connectionId: value ?? ''})}
                error={errors.connectionId ? I18n.get(errors.connectionId) : undefined}
            />

            <Stack gap={4}>
                <TextInput
                    required={true}
                    label={I18n.get('Hostname')}
                    description={I18n.get('The primary name on the certificate. A wildcard such as *.example.com is allowed.')}
                    placeholder={'example.com'}
                    value={values.domainName}
                    onChange={event => handleChange({domainName: event.currentTarget.value})}
                    error={errors.domainName ? I18n.get(errors.domainName) : undefined}
                />
                {/* ⚠️ THE ONE-LABEL RULE, AT THE POINT IT APPLIES.
                    Informational blue, not a warning — requesting the deeper
                    name directly is the CORRECT action and the copy says so. */}
                {coverageNote && (
                    <Alert color={'blue'} variant={'light'} p={'xs'}>
                        <Text size={'xs'}>{coverageNote}</Text>
                    </Alert>
                )}
            </Stack>

            <Stack gap={4}>
                <Text size={'sm'} fw={500}>{I18n.get('Additional names')}</Text>
                <Text size={'xs'} c={'dimmed'}>
                    {/* This field IS the answer to the wildcard constraint, so
                        it says so rather than sitting there unexplained. */}
                    {I18n.get('Other hostnames this same certificate should cover. This is how you cover a name a wildcard cannot reach, such as one two levels below its parent.')}
                </Text>
                {values.subjectAlternativeNames.map((name, index) => (
                    <Group key={index} gap={'xs'} align={'flex-start'} wrap={'nowrap'}>
                        <TextInput
                            style={{flex: 1}}
                            placeholder={'www.example.com'}
                            value={name}
                            onChange={(event) => {
                                const next = [...values.subjectAlternativeNames];
                                next[index] = event.currentTarget.value;
                                handleChange({subjectAlternativeNames: next});
                            }}
                            error={errors[`subjectAlternativeNames.${index}`]
                                ? I18n.get(errors[`subjectAlternativeNames.${index}`])
                                : undefined}
                        />
                        <ActionIcon
                            variant={'subtle'}
                            color={'red'}
                            aria-label={I18n.get('Remove this name')}
                            onClick={() => handleChange({
                                subjectAlternativeNames: values.subjectAlternativeNames
                                                               .filter((_, i) => i !== index),
                            })}
                        >
                            ×
                        </ActionIcon>
                    </Group>
                ))}
                <Group>
                    <Button
                        variant={'subtle'}
                        size={'compact-sm'}
                        onClick={() => handleChange({
                            subjectAlternativeNames: [...values.subjectAlternativeNames, ''],
                        })}
                    >
                        {I18n.get('Add another name')}
                    </Button>
                </Group>
            </Stack>

            <Select
                required={true}
                label={I18n.get('What will use this certificate')}
                description={I18n.get('This decides which region the certificate must be issued in.')}
                data={targetOptions}
                value={values.targetType}
                onChange={value => handleChange({
                    targetType: (value as CertificateTargetType) ?? 'UNSPECIFIED',
                })}
            />

            {/* ⚠️ THE REGION FIELD DISAPPEARS FOR CLOUDFRONT, replaced by the
                statement of the rule.

                Showing a region picker that is about to be ignored would invite
                an operator to choose eu-west-1 and believe they had. The
                backend refuses a contradicting region rather than silently
                overriding it, precisely so nobody is misled — and the form
                should not create the misunderstanding the backend then has to
                correct. */}
            {isCloudFront ? (
                <Alert color={'blue'} variant={'light'} title={I18n.get('Region')}>
                    <Text size={'sm'}>
                        {I18n.get('A certificate for a CloudFront distribution must be issued in')}
                        {' '}<strong>{CLOUDFRONT_REGION}</strong>{' '}
                        {I18n.get('— that is CloudFront\'s own requirement and it applies wherever the DNS for this name lives. This certificate will be issued there.')}
                    </Text>
                </Alert>
            ) : (
                <Select
                    required={true}
                    searchable={true}
                    label={I18n.get('Region')}
                    description={I18n.get('The certificate must be in the same region as the resource that will serve it.')}
                    placeholder={I18n.get('Choose a region')}
                    data={COMMON_REGIONS}
                    value={values.region || null}
                    onChange={value => handleChange({region: value ?? ''})}
                    error={errors.region ? I18n.get(errors.region) : undefined}
                />
            )}

            {/* ⚠️ THE ACM-GRANT CAVEAT, STATED BEFORE SUBMIT.

                The certificate declaration is made BY THE TENANT, never
                probed, so passing the connection filter does not prove the
                role actually holds acm:RequestCertificate. Rather than let
                that surface as a bare AccessDenied that reads like a Flexi
                bug, the form says what the role needs while the operator is
                still here to act on it.

                ⚠️ FNP-1280 narrowed the filter — it reads the certificate
                declaration now rather than the DNS one — but did NOT make it
                a proof, so this caveat stays exactly as load-bearing as it
                was. Until FNP-1276 updates the tenant-facing role
                instructions, this alert is the only place the console names
                the two ACM actions. */}
            <Alert color={'gray'} variant={'light'}>
                <Text size={'xs'}>
                    {I18n.get('The certificate is issued in your own AWS account, so the role this connection assumes needs acm:RequestCertificate and acm:DeleteCertificate. If it does not have them yet, this request will be refused and nothing will be created. Keep both in place afterwards — the provider re-checks the validation record for the life of the certificate.')}
                </Text>
            </Alert>

            <Group justify={'flex-end'}>
                <Button variant={'default'} onClick={onCancel} disabled={isSubmitting}>
                    {I18n.get('Cancel')}
                </Button>
                <Button onClick={handleSubmit} loading={isSubmitting} disabled={isSubmitting}>
                    {I18n.get('Request certificate')}
                </Button>
            </Group>

        </Stack>
    );
};
